import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ElementRef, ViewChild, HostListener, ChangeDetectorRef, NgZone} from '@angular/core';
import {FormControl} from "@angular/forms";
import {debounceTime, distinctUntilChanged, filter, of, Subject, switchMap} from "rxjs";
import {catchError, finalize, takeUntil, tap} from "rxjs/operators";
import {ProductDataService} from "../../../inventory/product/product.data.service";
import {HelperService} from "../../../../services/helper.service";

@Component({
    selector: 'app-search-field',
    templateUrl: './search-field.component.html',
    styleUrls: ['./search-field.component.scss'],
    standalone: false
})
export class SearchFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() set clearResults(value: any) {
    console.log('clear Results', value);
    this.filteredResult = [];
  }
  @Input() searchAbleDataService: any;
  @Input() selectedTextMarginRight?: string | number;
  @Input() searchAbleFunctionName: string = 'list';
  @Input() searchAbleParams: any = {
    page: 1,
    per_page: 200,
    sort_by: 'code',
    sort_order: 'desc',
    columns: [],
  };
  @Input() isStringParams: boolean = false;
  @Input() minLengthTerm = 2;
  @Input() searchPlaceHolder = 'Type to search';
  @Input() labelKey = 'title';
  @Input() rightLabelKey = '';
  @Input() secondaryLabelKey = '';
  @Input() rightLabelType = 'text';
  @Input() defaultSearchValue = '';
  @Input() selectedItemBackGroundColor = '#eeeeeeee';
  @Input() showSelectionDiv = false;
  @Input() selectedItem: any = "";
  @Input() disabled: boolean = false;

  @Output() onSelectValue = new EventEmitter<any>(true);
  @Output() onNoSelection = new EventEmitter<any>(true);
  @Output() onRemoveSelection = new EventEmitter<any>(true);

  @ViewChild('searchContainer') searchContainer!: ElementRef;
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('searchDropdown') searchDropdown!: ElementRef;

  // Search state
  searchCtrl = new FormControl();
  filteredResult: any[] = [];
  isLoading = false;
  hasSearched = false;
  isInputCurrentlyFocused = false;
  isFocused = false;
  selectedSearchResult: any = "";

  // Custom dropdown state
  showDropdown = false;
  highlightedIndex = -1;
  private isSelecting = false; // Flag to prevent API call during selection
  isInsideTable = false;
  dropdownPosition: { top: string; left: string; width: string } | null = null;

  private destroy$ = new Subject<void>();
  private currentSearchTerm = '';
  onBlurSelectionInputCounter = 0;

  constructor(
    public productDataService: ProductDataService,
    public helperService: HelperService,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    this.searchAbleDataService = this.productDataService;
  }

  get shouldShowDropdown(): boolean {
    const show = this.showDropdown && (this.isLoading || this.hasSearched);
    if (show && this.isInsideTable) {
      setTimeout(() => this.updateDropdownPosition(), 0);
    }
    return show;
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    if (this.isInsideTable && this.showDropdown) {
      this.updateDropdownPosition();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    if (this.isInsideTable && this.showDropdown) {
      this.updateDropdownPosition();
    }
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.elementRef.nativeElement.contains(event.target)) {
      return;
    }
    if (this.isLoading) {
      return;
    }
    this.showDropdown = false;
  }

  ngOnInit(): void {
    this.searchCtrl.setValue(this.defaultSearchValue);
    if (this.disabled) {
      this.searchCtrl.disable();
    }
    this.onInitSearchProduct();
    // Detect table context after a short delay
    setTimeout(() => this.detectTableContext(), 100);
  }

  private detectTableContext(): void {
    if (this.elementRef?.nativeElement) {
      let element: HTMLElement | null = this.elementRef.nativeElement;
      while (element && element !== document.body) {
        const tagName = element.tagName?.toUpperCase();
        if (tagName === 'TD' || tagName === 'TH' || tagName === 'TABLE' || tagName === 'TBODY' || tagName === 'TR') {
          this.isInsideTable = true;
          break;
        }
        element = element.parentElement;
      }
    }
  }

  private updateDropdownPosition(): void {
    // Use searchContainer (the full container) instead of searchInput for proper width
    if (!this.showDropdown || !this.searchContainer?.nativeElement) {
      this.dropdownPosition = null;
      return;
    }

    requestAnimationFrame(() => {
      if (this.searchContainer?.nativeElement) {
        const rect = this.searchContainer.nativeElement.getBoundingClientRect();
        if (rect.width > 0) {
          this.dropdownPosition = {
            top: `${rect.bottom + 6}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`
          };
          this.cdr.detectChanges();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.searchCtrl) {
      if (this.disabled) {
        this.searchCtrl.disable();
      } else {
        this.searchCtrl.enable();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onFocusSelectionInput() {
    this.isInputCurrentlyFocused = true;
    if (this.isInsideTable) {
      setTimeout(() => this.updateDropdownPosition(), 0);
    }
  }

  async onBlurSelectionInput() {
    this.isInputCurrentlyFocused = false;
    this.onBlurSelectionInputCounter++;
    const counter = this.onBlurSelectionInputCounter;
    await this.helperService.delay(400);

    if (counter == this.onBlurSelectionInputCounter && !this.isInputCurrentlyFocused) {
      if (this.isLoading) {
        return;
      }

      if (this.hasSearched && this.filteredResult && this.filteredResult.length > 0) {
        return;
      }

      this.showDropdown = false;

      const currentValue = this.searchCtrl.value;
      if (typeof currentValue === 'string' && currentValue) {
        this.onNoSelection.emit(currentValue);
      }
    }
  }

  // Keyboard navigation
  onKeyDown(event: KeyboardEvent) {
    if (!this.showDropdown || !this.filteredResult?.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredResult.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;
      case 'Escape':
        this.showDropdown = false;
        this.highlightedIndex = -1;
        break;
    }
  }

  // Select highlighted item (on Enter key)
  selectHighlightedItem() {
    if (this.highlightedIndex >= 0 && this.filteredResult?.[this.highlightedIndex]) {
      this.selectItem(this.filteredResult[this.highlightedIndex]);
    }
  }

  // Select item from dropdown
  selectItem(item: any) {
    this.isSelecting = true;
    this.selectedSearchResult = item;
    this.selectedItem = item;
    this.searchCtrl.setValue(item[this.labelKey] || item['title'] || '', { emitEvent: false });
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.onSelectValue.emit(this.selectedItem);
    setTimeout(() => this.isSelecting = false, 100);
  }

  onInitSearchProduct() {
    this.searchCtrl.valueChanges
      .pipe(
        tap((res) => {
          if (this.isSelecting) {
            return;
          }

          if (res === null || res === '' || (typeof res === 'string' && res.length < this.minLengthTerm)) {
            this.filteredResult = [];
            this.hasSearched = false;
            this.isLoading = false;
            this.currentSearchTerm = '';
            if (!this.isInputCurrentlyFocused) {
              this.showDropdown = false;
            }
            this.highlightedIndex = -1;
          }
        }),
        filter(res => {
          if (this.isSelecting) {
            return false;
          }
          return res !== null && typeof res === 'string' && res.length >= this.minLengthTerm;
        }),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.filteredResult = [];
          this.isLoading = true;
          this.hasSearched = false;
          this.showDropdown = true;
          this.highlightedIndex = -1;
          if (this.isInsideTable) {
            setTimeout(() => this.updateDropdownPosition(), 0);
          }
          this.cdr.detectChanges();
        }),
        switchMap((value: any) => {
          this.currentSearchTerm = value;
          let params: any;
          if (this.isStringParams) {
            params = value;
          } else {
            params = { ...this.searchAbleParams, search: value, loadingService: 0 };
          }
          return this.searchAbleDataService[this.searchAbleFunctionName](params)
            .pipe(
              catchError(() => of({ data: { data: [] } })),
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((data: any) => {
        this.ngZone.run(() => {
          const results = data.data.data ? data.data.data : data.data;

          this.filteredResult = this.applySmartFilter(results);
          this.hasSearched = true;
          this.showDropdown = true;
          this.isLoading = false;
          this.highlightedIndex = this.filteredResult?.length > 0 ? 0 : -1;
          this.onBlurSelectionInputCounter++;

          if (this.isInsideTable) {
            setTimeout(() => this.updateDropdownPosition(), 0);
          }

          this.cdr.detectChanges();
        });
      });
  }

  get selectedTextMarginRightValue(): string | null {
    const v = this.selectedTextMarginRight;
    if (v === null || v === undefined || v === '') return null;
    return typeof v === 'number' ? `${v}px` : v;
  }

  clearSelection() {
    this.selectedSearchResult = "";
    this.selectedItem = "";
    this.filteredResult = [];
    this.hasSearched = false;
    this.currentSearchTerm = '';
    this.searchCtrl.setValue('');
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.onRemoveSelection.emit(true);
  }

  private applySmartFilter(results: any[]): any[] {
    // Return results as-is if no search term or no results
    if (!this.currentSearchTerm || !results?.length) return results;
    const term = this.currentSearchTerm.toLowerCase().trim();
    if (!term) return results;

    // Only sort results (don't filter - trust the API)
    // This puts items starting with the search term first
    return [...results].sort((a, b) => {
      const labelA = (a[this.labelKey] || '').toString().toLowerCase();
      const labelB = (b[this.labelKey] || '').toString().toLowerCase();
      const aStartsWith = labelA.startsWith(term) ? 0 : 1;
      const bStartsWith = labelB.startsWith(term) ? 0 : 1;
      return aStartsWith - bStartsWith;
    });
  }
}
